import type {
  EventId,
  PlanEvent,
} from "../../../../shared/types";
import type { RecurrenceToastState } from "./RecurrenceActionToast";
import { EventItem } from "../../../events/components/event-item/EventItem";
import { useEditingEventDelete } from "./useEditingEventDelete";

import type { EventFullData } from "../../../events/types/event-form-types";

interface EditingEventPanelProps {
  readonly editingEvent: PlanEvent;
  readonly events: PlanEvent[];
  readonly onDeleteEvent?: (id: EventId) => void;
  readonly onUpdateEvent?: (
    id: EventId,
    updates: Partial<
      Pick<
        PlanEvent,
        | "title"
        | "date"
        | "startTime"
        | "endTime"
        | "notes"
        | "description"
        | "tags"
        | "recurrence"
        | "reminderMinutes"
        | "category"
      >
    >,
  ) => void;
  readonly onSetEditingEvent: (event: PlanEvent | null) => void;
  readonly onSetShowFullEditForm?: (show: boolean) => void;
  readonly onSetPendingResync: (event: PlanEvent | null) => void;
  readonly onSetRecurrenceToast: (toast: RecurrenceToastState | null) => void;
  readonly onDuplicate?: (event: PlanEvent) => void;
  readonly onAddRecurrenceException?: (baseId: EventId, date: string) => void;
  readonly onUndoEvent?: () => void;
  readonly onAddEventFull?: (data: EventFullData) => void;
  readonly readOnly?: boolean;
}

function parseTimeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function minutesToTime(total: number): string {
  const clamped = Math.max(0, Math.min(total, 23 * 60 + 59));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

const QUICK_ADJUST_BTN =
  "px-2 py-1.5 text-[11px] font-medium rounded-md border border-border text-ink-muted hover:bg-surface-alt focus:outline-none transition-colors";

function QuickTimeAdjust({
  editingEvent,
  onUpdateEvent,
  onSetEditingEvent,
}: {
  readonly editingEvent: PlanEvent;
  readonly onUpdateEvent: NonNullable<EditingEventPanelProps["onUpdateEvent"]>;
  readonly onSetEditingEvent: (event: PlanEvent | null) => void;
}) {
  const handleMove = (deltaMinutes: number) => {
    const start = parseTimeToMinutes(editingEvent.startTime!);
    const end = parseTimeToMinutes(editingEvent.endTime!);
    const newStart = Math.max(0, Math.min(start + deltaMinutes, 23 * 60 + 59));
    const newEnd = Math.max(0, Math.min(end + deltaMinutes, 23 * 60 + 59));
    const updates = {
      startTime: minutesToTime(newStart),
      endTime: minutesToTime(newEnd),
    };
    onUpdateEvent(editingEvent.id, updates);
    onSetEditingEvent({ ...editingEvent, ...updates });
  };

  const handleResize = (deltaMinutes: number) => {
    const start = parseTimeToMinutes(editingEvent.startTime!);
    const end = parseTimeToMinutes(editingEvent.endTime!);
    const newEnd = Math.max(start + 15, Math.min(end + deltaMinutes, 23 * 60 + 59));
    const updates = { endTime: minutesToTime(newEnd) };
    onUpdateEvent(editingEvent.id, updates);
    onSetEditingEvent({ ...editingEvent, ...updates });
  };

  return (
    <div className="flex flex-col gap-1.5 px-1">
      <span className="text-[11px] font-semibold text-ink-muted uppercase tracking-wide">
        Ajustement rapide
      </span>
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-ink-faint w-14 shrink-0">Décaler</span>
        <button type="button" className={QUICK_ADJUST_BTN} onClick={() => handleMove(-30)}>
          −30min
        </button>
        <button type="button" className={QUICK_ADJUST_BTN} onClick={() => handleMove(-15)}>
          −15min
        </button>
        <button type="button" className={QUICK_ADJUST_BTN} onClick={() => handleMove(15)}>
          +15min
        </button>
        <button type="button" className={QUICK_ADJUST_BTN} onClick={() => handleMove(30)}>
          +30min
        </button>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-ink-faint w-14 shrink-0">Durée</span>
        <button type="button" className={QUICK_ADJUST_BTN} onClick={() => handleResize(-15)}>
          Raccourcir
        </button>
        <button type="button" className={QUICK_ADJUST_BTN} onClick={() => handleResize(15)}>
          Prolonger
        </button>
      </div>
    </div>
  );
}

export function EditingEventPanel({
  editingEvent,
  events,
  onDeleteEvent,
  onUpdateEvent,
  onSetEditingEvent,
  onSetShowFullEditForm,
  onSetPendingResync,
  onSetRecurrenceToast,
  onDuplicate,
  onAddRecurrenceException,
  onUndoEvent,
  onAddEventFull,
  readOnly,
}: EditingEventPanelProps) {
  const { handleDelete } = useEditingEventDelete({
    events,
    onDeleteEvent: onDeleteEvent ?? (() => {}),
    onSetEditingEvent,
    onSetRecurrenceToast,
    onAddRecurrenceException,
    onUndoEvent,
  });

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-semibold text-ink-muted uppercase tracking-wide">
          {readOnly ? "Détails" : "Modifier"}
        </span>
        <button
          type="button"
          onClick={() => onSetEditingEvent(null)}
          className="text-xs text-ink-faint hover:text-ink focus:outline-none"
        >
          ✕ Fermer
        </button>
      </div>
      <EventItem
        event={editingEvent}
        defaultExpanded
        onDelete={readOnly ? undefined : handleDelete}
        onUpdate={readOnly ? undefined : (id, updates) => {
          onUpdateEvent?.(id, updates);
          onSetEditingEvent({ ...editingEvent, ...updates });
        }}
        readOnly={readOnly}
      />
      {onSetShowFullEditForm && (
        <button
          type="button"
          onClick={() => onSetShowFullEditForm(true)}
          className="w-full py-2 text-xs font-semibold bg-accent text-white rounded-lg hover:opacity-90 focus:outline-none transition-opacity"
        >
          Modifier l'événement
        </button>
      )}
      {onUpdateEvent && editingEvent.startTime && editingEvent.endTime && (
        <QuickTimeAdjust
          editingEvent={editingEvent}
          onUpdateEvent={onUpdateEvent}
          onSetEditingEvent={onSetEditingEvent}
        />
      )}
      {onAddEventFull && onDuplicate && (
        <button
          type="button"
          onClick={() => onDuplicate(editingEvent)}
          className="w-full py-2 text-xs font-medium text-ink-muted border border-border rounded-lg hover:bg-surface-alt focus:outline-none transition-colors"
        >
          Dupliquer
        </button>
      )}
      {editingEvent.detachedFromId && onSetPendingResync && (
        <button
          type="button"
          onClick={() => onSetPendingResync(editingEvent)}
          className="w-full py-2 text-xs font-medium text-accent border border-accent/30 rounded-lg hover:bg-accent/10 focus:outline-none transition-colors"
        >
          Rattacher à la série
        </button>
      )}
      {onDeleteEvent && (
        <button
          type="button"
          onClick={() => handleDelete(editingEvent.id)}
          className="w-full py-2 text-xs font-medium text-danger border border-danger/30 rounded-lg hover:bg-danger/10 focus:outline-none transition-colors"
        >
          Supprimer l'événement
        </button>
      )}
    </div>
  );
}
